import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, clearToken, getToken } from "./api";

const AuthContext = createContext(null);

/**
 * Single source of truth for "where does this user belong?".
 *
 * Login, Signup, AcceptInvite and ProtectedRoute all import this helper
 * so we never disagree about where to send an authenticated user.
 */
export function roleHome(user) {
  if (!user) return "/app/login";
  if (user.role === "staff") return "/app/my-training";
  // owner + demo + any future admin role land on the workspace dashboard
  return "/app/dashboard";
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    status: "checking",
    user: null,
    business: null,
    organisations: [],
  });

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setState({ status: "guest", user: null, business: null, organisations: [] });
      return;
    }
    try {
      const data = await api.get("/auth/me");
      setState({
        status: "authed",
        user: data.user,
        business: data.business,
        organisations: data.organisations || [],
      });
    } catch {
      clearToken();
      setState({ status: "guest", user: null, business: null, organisations: [] });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const finish = (data) => {
    setToken(data.access_token);
    setState((s) => ({
      ...s,
      status: "authed",
      user: data.user,
      business: data.business,
    }));
    // Re-hydrate the organisations list from /auth/me — signup/login/etc.
    // don't return it directly.
    refresh();
    return data;
  };

  const login = async (email, password) =>
    finish(await api.post("/auth/login", { email, password }));
  const signup = async (payload) => finish(await api.post("/auth/signup", payload));
  const demoLogin = async () => finish(await api.post("/auth/demo"));
  const acceptInvite = async (token, password) =>
    finish(await api.post("/auth/accept-invite", { token, password }));

  const switchOrg = async (organisation_id) => {
    const data = await api.post("/auth/switch-org", { organisation_id });
    setToken(data.access_token);
    setState((s) => ({
      ...s,
      status: "authed",
      user: data.user,
      business: data.business,
    }));
    // Reload the app so every page-scoped data fetch runs against the
    // new tenant. Simple + predictable — no chance of cache bleed.
    if (typeof window !== "undefined") {
      window.location.assign("/app/dashboard");
    }
    return data;
  };

  const logout = () => {
    clearToken();
    setState({ status: "guest", user: null, business: null, organisations: [] });
  };

  return (
    <AuthContext.Provider
      value={{ ...state, login, signup, demoLogin, acceptInvite, logout, refresh, switchOrg }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
